import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardActionArea, CardContent, Grid,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupsIcon from '@mui/icons-material/Groups';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ChatIcon from '@mui/icons-material/Chat';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import NotificationsIcon from '@mui/icons-material/Notifications';
import axios from 'axios';
import { API_URL } from '../api';
import { getCached, setCached } from '../cache';

const ADMIN_ROLES = ['ADMIN', 'PRESIDENT'];

const HomePage = ({ currentUser }) => {
  const navigate = useNavigate();
  const [memberCount, setMemberCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Show the count from last visit immediately (people count barely
    // changes minute to minute), then refresh quietly underneath.
    const cached = getCached('people');
    if (cached && cached.data) setMemberCount(cached.data.length);

    axios.get(`${API_URL}/api/people`)
      .then((res) => {
        if (!cancelled) setMemberCount(res.data.length);
        setCached('people', res.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const firstName = currentUser?.name ? currentUser.name.split(' ')[0] : 'Family member';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);

  const features = [
    {
      title: 'Family Tree',
      desc: memberCount
        ? `Explore all ${memberCount} members of our family — from Mwendwa Kitavi to today.`
        : 'Explore the family — from Mwendwa Kitavi to today.',
      icon: <AccountTreeIcon sx={{ fontSize: 32 }} />,
      path: '/tree',
    },
    {
      title: 'Family Events',
      desc: 'Weddings, burials, dowry, and family projects — with contributions.',
      icon: <CelebrationIcon sx={{ fontSize: 32 }} />,
      path: '/events',
    },
    {
      title: 'Family Chat',
      desc: 'Talk with the whole family and message members privately.',
      icon: <ChatIcon sx={{ fontSize: 32 }} />,
      path: '/chat',
    },
    {
      title: 'Market & Talents',
      desc: 'Buy from family, sell to family — and showcase what you do best.',
      icon: <StorefrontIcon sx={{ fontSize: 32 }} />,
      path: '/market',
    },
    {
      title: 'My Family',
      desc: 'Your place in the tree. Your photo, your spouse, your children.',
      icon: <GroupsIcon sx={{ fontSize: 32 }} />,
      path: '/family',
    },
    {
      title: 'Our History',
      desc: 'The story of Chief Mwendwa Kitavi and the generations after him.',
      icon: <AutoStoriesIcon sx={{ fontSize: 32 }} />,
      path: '/history',
    },
    {
      title: 'Settings',
      desc: 'Change your password and manage message alerts.',
      icon: <NotificationsIcon sx={{ fontSize: 32 }} />,
      path: '/notifications',
    },
    ...(isAdmin ? [{
      title: 'Admin',
      desc: 'Manage family accounts, roles, and claims.',
      icon: <AdminPanelSettingsIcon sx={{ fontSize: 32 }} />,
      path: '/admin',
    }] : []),
  ];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
        {greeting}, {firstName} 👋
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Welcome home to Mbaa Mwendwa.
      </Typography>

      <Grid container spacing={2}>
        {features.map((f) => (
          <Grid item xs={12} sm={6} key={f.title}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', borderColor: '#e6e6e6' }}>
              <CardActionArea onClick={() => navigate(f.path)} sx={{ p: 1, height: '100%' }}>
                <CardContent>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: 2.5, mb: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: '#D1FAE5', color: '#047857',
                  }}>
                    {f.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{f.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{f.desc}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default HomePage;
